import { observer } from 'mobx-react-lite';

import { XStack, YStack } from '@/components/ui';
import { Slider } from '@/components/ui/Slider';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { IMPORTANCE_MAX } from '@/features/home/card-importance';
import { ImportanceThresholdSlider } from '@/features/home/components/ImportanceThresholdSlider';
import { useMinCardImportance } from '@/features/home/use-min-card-importance';
import { DeliveryChannelDropdown } from '@/features/msg-channels/common/DeliveryChannelDropdown';
import { MESSAGING_DELIVERY_OFF } from '@/features/msg-channels/common/msg-channels.types';

import { canUseProactiveAgent } from '../proactive-agent-access';
import { SettingsSection } from '../settings-section';
import { useBorderlineCards } from '../use-borderline-cards';
import { useCardChannelDelivery } from '../use-card-channel-delivery';

export const ProactivitySettings = observer(function ProactivitySettings() {
  const { minImportance } = useMinCardImportance();
  const { borderlineCards, setBorderlineCards } = useBorderlineCards();
  const delivery = useCardChannelDelivery();

  if (!canUseProactiveAgent()) return null;

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Proactivity</SettingsSection.Title>
        <SettingsSection.Description>
          Choose which cards Littlebird shows you.
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <SettingsSection.Item searchId="proactivity.importance">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Minimum card importance
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              Littlebird rates some cards from 0 to 10 by importance. Cards
              rated below this are hidden. Cards without a rating always show.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <YStack className="w-72 shrink-0 gap-2">
            <ImportanceThresholdSlider />
            <XStack className="justify-end text-xs tabular-nums text-foreground-muted">
              <span>{minImportance}</span>
            </XStack>
          </YStack>
        </SettingsSection.Item>
        <SettingsSection.Item searchId="proactivity.borderline-cards">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Borderline cards
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              How readily your proactive agent&apos;s routines create cards for
              things that may or may not matter to you. Things that clearly
              matter, or clearly do not, are not affected.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <YStack className="w-72 shrink-0 gap-2">
            <Slider
              aria-label="Borderline cards"
              value={[borderlineCards]}
              min={0}
              max={100}
              step={1}
              onValueChange={([value]) => setBorderlineCards(value)}
            />
            <XStack className="justify-between text-xs tabular-nums text-foreground-muted">
              <span>Fewer</span>
              <span>{borderlineCards}</span>
              <span>More</span>
            </XStack>
          </YStack>
        </SettingsSection.Item>
        {hasFeature(FEATURES.PROACTIVE_CARDS_IN_CHANNELS) ? (
          <SettingsSection.Item searchId="proactivity.card-delivery">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Send cards to a messaging channel
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Littlebird also sends cards rated at least this important to
                this channel. Cards without a rating are not sent.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <YStack className="w-72 shrink-0 items-end gap-2">
              <DeliveryChannelDropdown
                channel={delivery.channel}
                onChange={delivery.setChannel}
              />
              {delivery.channel === MESSAGING_DELIVERY_OFF ? null : (
                <>
                  <Slider
                    aria-label="Minimum importance to send a card"
                    className="w-full"
                    value={[delivery.minImportance]}
                    min={0}
                    max={IMPORTANCE_MAX}
                    step={1}
                    onValueChange={([value]) =>
                      delivery.setMinImportance(value)
                    }
                  />
                  <span className="text-xs tabular-nums text-foreground-muted">
                    {delivery.minImportance}
                  </span>
                </>
              )}
            </YStack>
          </SettingsSection.Item>
        ) : null}
      </SettingsSection.Group>
    </SettingsSection.Root>
  );
});
